/**
 * UUIDv7 (RFC 9562): 48-bit unix-ms timestamp + randomness.
 * Every synced row gets its id on the device that created it, so offline
 * inserts never need a server round-trip and ids sort by creation time.
 * Within one millisecond the 12-bit rand_a field is used as a counter so ids
 * generated in a tight loop stay strictly increasing.
 */
let lastMs = -1;
let seq = 0;

type RandomFill = (bytes: Uint8Array<ArrayBuffer>) => Uint8Array<ArrayBuffer>;
const cryptoFill: RandomFill = (bytes) => crypto.getRandomValues(bytes);

export function uuidv7(nowMs: number = Date.now(), fill: RandomFill = cryptoFill): string {
  const bytes = fill(new Uint8Array(16));
  let ms = nowMs;
  if (ms > lastMs) {
    // Start the counter in the lower half so a burst has room to grow.
    seq = ((bytes[6]! & 0x07) << 8) | bytes[7]!;
  } else {
    ms = lastMs;
    seq += 1;
    if (seq > 0xfff) {
      ms += 1;
      seq = 0;
    }
  }
  lastMs = ms;

  bytes[0] = Math.floor(ms / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(ms / 2 ** 32) & 0xff;
  bytes[2] = (ms >>> 24) & 0xff;
  bytes[3] = (ms >>> 16) & 0xff;
  bytes[4] = (ms >>> 8) & 0xff;
  bytes[5] = ms & 0xff;
  bytes[6] = 0x70 | ((seq >>> 8) & 0x0f);
  bytes[7] = seq & 0xff;
  bytes[8] = 0x80 | (bytes[8]! & 0x3f);

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function uuidv7Timestamp(id: string): number {
  return parseInt(id.replace(/-/g, '').slice(0, 12), 16);
}
